import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { AuditFilters, AuditRecord } from '../../../core/models/audit.model';
import { AuditService } from '../../../core/services/audit.service';
import { PageHeaderComponent } from '../../../shared/page-header/page-header';
import { UiButtonComponent } from '../../../shared/ui-button/ui-button';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell';
import { DataTablePaginado } from '../../../shared/data-table-paginado/data-table-paginado';
import { CardListComponent } from '../../../shared/card-list/card-list';
import { MobileService } from '../../../core/services/mobile.service';
import { LoadingComponent } from '../../../shared/loading/loading';
import {
  TableActionEvent,
  TableColumn,
  TablePageEvent,
} from '../../../shared/data-table-paginado/data-table-paginado.models';
import dayjs from 'dayjs';
import { isValidDateInput } from '../../../core/utils/validation.utils';

@Component({
  selector: 'app-auditoria',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    PageHeaderComponent,
    UiButtonComponent,
    ModalShellComponent,
    DataTablePaginado,
    CardListComponent,
    LoadingComponent,
  ],
  templateUrl: './auditoria.html',
  styleUrl: './auditoria.css',
})
export class AuditoriaComponent {
  private readonly auditService = inject(AuditService);
  protected readonly mobileService = inject(MobileService);

  protected records: AuditRecord[] = [];
  protected filters: AuditFilters = {};
  protected loading = false;
  protected errorMessage = '';
  protected readonly maxDate = dayjs().format('YYYY-MM-DD');
  protected page = 1;
  protected readonly pageSize = 25;
  protected totalItems = 0;

  protected readonly columns: TableColumn<AuditRecord>[] = [
    {
      key: 'fechaCreacion',
      label: 'Fecha',
      type: 'datetime',
      formatter: (value) => this.formatAuditDate(value),
      width: '150px',
    },
    {
      key: 'usuario',
      label: 'Usuario',
      valueGetter: (row) => row.usuario ?? 'Sistema',
      width: '150px',
    },
    {
      key: 'accion',
      label: 'Acción',
      type: 'badge',
      badgeClass: (_value, row) => this.actionClass(row.accion),
    },
    {
      key: 'nombreEntidad',
      label: 'Entidad',
    },
    {
      key: 'identificadorEntidad',
      label: 'Identificador',
      width: '260px',
      maxWidth: '260px',
      truncate: true,
      formatter: (value) => value ?? '—',
    },
    {
      key: 'detalles',
      label: 'Detalles',
      width: '260px',
      maxWidth: '260px',
      truncate: true,
      formatter: (value) => value ?? '—',
    },
    {
      key: '__actions',
      label: 'Acciones',
      type: 'actions',
      align: 'center',
      width: '80px',
      actions: [
        {
          id: 'view',
          label: 'Ver detalles',
          ariaLabel: 'Ver detalles de auditoría',
          icon: 'fa-solid fa-eye',
        },
      ],
    },
  ];

  protected showModalDetails = false;
  protected selectedRecord: AuditRecord | null = null;

  constructor() {
    this.loadRecords();
  }

  protected loadRecords(): void {
    this.loading = true;
    this.errorMessage = '';
    this.auditService
      .getRecords(this.filters, this.page, this.pageSize)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.records = result.items;
          this.page = result.page;
          this.totalItems = result.totalItems;
        },
        error: (error: unknown) => {
          this.errorMessage = this.getErrorMessage(error);
          this.records = [];
        },
      });
  }

  protected applyFilters(): void {
    if (
      (this.filters.desde && !isValidDateInput(this.filters.desde)) ||
      (this.filters.hasta && !isValidDateInput(this.filters.hasta))
    ) {
      this.errorMessage = 'Las fechas de auditoría no son válidas.';
      return;
    }
    if (this.filters.desde && this.filters.hasta && this.filters.desde > this.filters.hasta) {
      this.errorMessage = 'La fecha inicial no puede ser posterior a la fecha final.';
      return;
    }
    if (
      (this.filters.desde && this.filters.desde > this.maxDate) ||
      (this.filters.hasta && this.filters.hasta > this.maxDate)
    ) {
      this.errorMessage = 'No se pueden consultar fechas posteriores a la fecha actual.';
      return;
    }
    this.page = 1;
    this.loadRecords();
  }

  protected clearFilters(): void {
    this.filters = {};
    this.applyFilters();
  }

  protected onPageChange(event: TablePageEvent): void {
    this.page = event.page;
    this.loadRecords();
  }

  protected onAction(event: TableActionEvent<AuditRecord>): void {
    if (event.action === 'view') this.viewDetails(event.row);
  }

  protected actionClass(action: string): string {
    return action.toLowerCase().replaceAll('_', '-');
  }

  private getErrorMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'error' in error) {
      const response = error.error as { message?: string } | null;
      if (response?.message) return response.message;
    }
    return 'No fue posible cargar los registros de auditoría.';
  }

  protected viewDetails(record: AuditRecord): void {
    this.showModalDetails = true;
    this.selectedRecord = record;
  }

  protected closeDetails(): void {
    this.showModalDetails = false;
    this.selectedRecord = null;
  }

  protected formatDetails(details: string | null): string {
    if (!details) return 'Sin detalles adicionales.';

    try {
      const parsed = JSON.parse(details) as unknown;
      return JSON.stringify(this.expandNestedJson(parsed), null, 2);
    } catch {
      return details;
    }
  }

  private formatAuditDate(value: unknown): string {
    if (typeof value !== 'string' || !value) return '—';
    return value.replace('T', ' ').slice(0, 16);
  }

  private expandNestedJson(value: unknown): unknown {
    if (Array.isArray(value)) return value.map((item) => this.expandNestedJson(item));

    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, item]) => [key, this.expandNestedJson(item)]),
      );
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;

      try {
        return this.expandNestedJson(JSON.parse(trimmed) as unknown);
      } catch {
        return value;
      }
    }

    return value;
  }
}
